import { PaymentIntent, RefundCharge } from 'types/stripe'

import Stripe from 'stripe'
import { PaymentHistory } from '../gql/generated-types/custom-graphql'
import { getDuplicatePaymentIntent, PaymentIntentDto } from '../apis/paymentIntent'
import {
	RETURNED_E_CHECK,
	checkScratchPaymentMethods,
	echeckScratchPaymentMethods
} from '../constants'

const failureCodesToExclude = ['bank_account_unusable', 'payment_method_skip_verification_failed']

const isFailedCharge = (charge) =>
	charge?.transaction_status === 'failed' ||
	charge?.transactionStatus === 'failed' ||
	charge?.status === 'failed'

export const isFailedCheckCharge = (charge) =>
	echeckScratchPaymentMethods.indexOf(charge?.metadata?.scratchPaymentMethod) > -1 &&
	isFailedCharge(charge) &&
	!failureCodesToExclude.includes(charge?.failure_code || charge?.failureCode)

export const isFailedCheckScan = (charge) =>
	charge?.metadata?.scratchPaymentMethod === 'check_scan' && isFailedCharge(charge)

/**
 * Check if a payment already exists on the same day, for the same client, practice and amount
 * @returns {boolean} Payment exists
 */
export const checkPaymentAlreadyExists = async (
	clientID: string,
	practiceId: string,
	amountInCents: number
) => {
	try {
		const response = await getDuplicatePaymentIntent(clientID, practiceId, amountInCents)
		return response?.data?.exists === true
	} catch (error) {
		// Fail-open: never block a payment because the duplicate lookup failed.
		console.error('checkPaymentAlreadyExists: duplicate lookup failed', error)
		return false
	}
}

// This method should be used after appending returned charge to the list of charges
// and while doing the appending process, a boolean flag `returnedECheck` is set to true
// Kept on the wider list: a returned check_scan is unreachable today (returned checks come from
// Action Needed eCheck items), and narrowing this would leave such a row with a blank status.
export const isReturnedECheckCharge = (charge) =>
	checkScratchPaymentMethods.indexOf(charge?.metadata?.scratchPaymentMethod) > -1 &&
	charge?.status === RETURNED_E_CHECK

export const isDisputedCharge = (charge, paymentIntent) =>
	charge?.disputed || paymentIntent?.disputed

/**
 * Reports a card hold that is not fully captured yet.
 * @param charge - The Stripe charge
 * @returns 'partially_captured' or 'uncaptured' for a hold, otherwise undefined
 */
export const getHoldStatus = (charge) => {
	if (charge?.captured === false && charge?.amount_captured > 0) return 'partially_captured'
	if (charge?.amount_captured === 0 && charge?.status === 'succeeded') return 'uncaptured'
	return undefined
}

/**
 * Caps a principal at what a Stripe charge has captured, so a hold only counts its captured part.
 * @param charge - The Stripe charge, or a row that is not a charge
 * @param principal - The principal in cents
 * @returns The principal, capped at the captured amount while the charge is not fully captured
 */
export const capAtCapturedAmount = (
	charge: { captured?: boolean; amount_captured?: number },
	principal: number
): number =>
	charge?.captured === false ? Math.min(principal, charge.amount_captured ?? 0) : principal

export const transactionStatus = (paymentIntent, charge) => {
	let status = charge?.status || paymentIntent.status
	// here we map the status of scratch loan payment requests
	if (paymentIntent?.metadata?.scratchpayLoanUniqueId != null) {
		const { scratchpayLoanStatus } = paymentIntent.metadata
		if (scratchpayLoanStatus === 'finalized') {
			status = 'succeeded'
		} else if (scratchpayLoanStatus === 'declined') {
			status = 'failed'
		} else {
			status = 'pending'
		}
	}

	if (paymentIntent.transactionStatus) {
		// If it's gql
		if (isRevertedPayment(paymentIntent)) {
			return 'reversal'
		}
		return paymentIntent.transactionStatus
	}

	if (paymentIntent.object === 'refund') {
		if (paymentIntent.refundType === 'reversal') {
			status = 'reversal'
		} else {
			status = 'refunded-transactions'
		}
	}

	status = getHoldStatus(charge) ?? status

	if (paymentIntent.originalStatus === 'failed') {
		status = 'failed'
	}

	if (paymentIntent.status === 'canceled') {
		status = 'failed'
	}

	return status
}

export const getSumPaidAmount = (charges: PaymentIntent[]) =>
	charges.reduce((a: number, v: PaymentIntent) => {
		const charge = v.charges?.data[0]
		const status = transactionStatus(v, charge)
		let amount = 0
		if (['paid', 'succeeded'].includes(status)) {
			if (isSurchargePayment(v)) {
				amount = Number(v.metadata?.amountWithoutSurcharge ?? 0)
			} else {
				amount = v.amount!
			}
		}
		return (a += capAtCapturedAmount(v, amount))
	}, 0)

export const getSumRefundedAmount = (charges: PaymentIntent[]) =>
	charges.reduce((a: number, v: PaymentIntent | RefundCharge) => {
		let refundedAmount = 0
		if (v.object === 'refund') {
			if (isSurchargePayment(v)) {
				refundedAmount = Number(v.metadata?.refundAmountWithoutSurcharge || 0)
			} else {
				refundedAmount = v.amount
			}
		}
		return (a += refundedAmount)
	}, 0)

export const getSumPendingAmount = (charges) =>
	charges.reduce((a, v) => {
		const charge = v.charges?.data[0]
		const status = transactionStatus(v, charge)
		const amount = ['open', 'pending', 'requires_payment_method', 'uncollectible'].includes(status)
			? v.amount
			: 0
		return (a += amount)
	}, 0)

export const getSumFailedECheckAmount = (charges) =>
	charges.reduce((a, v) => {
		const charge = v.charges?.data[0]
		return (a += isFailedCheckCharge(charge || v) ? (v?.amount ?? 0) : 0)
	}, 0)

// Daily Totals and the charge-list resume count failed checks: the PIMS already recorded them.
export const getSumFailedCheckScanAmount = (charges) =>
	charges.reduce((a, v) => {
		const charge = v.charges?.data[0]
		return (a += isFailedCheckScan(charge || v) ? (v?.amount ?? 0) : 0)
	}, 0)

export const getSumReturnedECheckAmount = (charges) =>
	charges.reduce((a, v) => {
		const charge = v.charges?.data[0]
		return (a += isReturnedECheckCharge(charge || v) ? (v?.amount ?? 0) : 0)
	}, 0)

export const getSumFailedAmount = (charges) =>
	charges.reduce((a, v) => {
		const charge = v.charges?.data[0]
		const status = transactionStatus(v, charge)
		return (a +=
			status === 'failed' && !isFailedCheckCharge(charge) && !isReturnedECheckCharge(charge)
				? v.amount
				: 0)
	}, 0)

export const getSumUncapturedAmount = (charges) =>
	charges.reduce((a, v) => {
		const charge = v.charges?.data[0]
		const status = transactionStatus(v, charge)
		return (a += status === 'uncaptured' ? v.amount : 0)
	}, 0)

export const isAchPaymentRequest = (charge) =>
	charge.metadata?.scratchPaymentMethod === 'payment_request' &&
	charge.payment_method_details?.type === 'us_bank_account'

export const isInteracPaymentType = (charge) =>
	charge?.payment_method_details?.type === 'interac_present' || // Payment History v1
	charge?.paymentMethodType === 'interac_present' // Payment History v2

export const isSurchargePayment = (
	paymentIntent: PaymentIntent | RefundCharge | PaymentHistory | Stripe.Charge
) => {
	if ('surcharge' in paymentIntent) {
		return paymentIntent.surcharge ?? false
	}
	return paymentIntent?.metadata?.surcharge === 'true'
}

/**
 * Donation details extracted from a payment's metadata. Donations are
 * non-refundable and must be excluded from refundable amounts.
 */
export type DonationInfo = {
	/** True when a positive donation amount is present on the payment */
	hasDonation: boolean
	/**
	 * True when `metadata.donationOptedIn === 'true'`. Refund rows inherit the
	 * original charge's metadata but have a zero donation amount, so use this
	 * (not `hasDonation`) to detect donation context on refunds (DASH-9097).
	 */
	donationOptedIn: boolean
	/** Donation amount in cents */
	donationAmount: number
	/** Name of the charity the donation was made to, if available */
	donationCharityName?: string
}

/** Stripe PI metadata fields used when parsing donation details (DASH-8243). */
export type DonationMetadata = {
	donationOptedIn?: string
	donationAmount?: string
	donationCharityName?: string
}

const toFiniteCents = (value: number | null | undefined): number | null =>
	typeof value === 'number' && Number.isFinite(value) ? value : null

const parseFiniteCents = (value: string | undefined): number | null => {
	if (!value) return null
	const parsed = parseInt(value, 10)
	return Number.isFinite(parsed) ? parsed : null
}

export type DonationInfoSource =
	| {
			donationAmount?: number | null
			metadata?: DonationMetadata | null
	  }
	| null
	| undefined

/**
 * Reads donation info from a payment/charge's metadata. Donation fields are set
 * on the PaymentIntent metadata (DASH-8243) as `donationOptedIn`,
 * `donationAmount` (cents), and `donationCharityName`. Stripe metadata values are
 * always strings, so the amount is parsed back to a number here.
 *
 * @param source - Any payment-like object exposing a `metadata` map (Stripe.Charge,
 *   PaymentHistory, or the refund record passed to the refund modals).
 * @returns {DonationInfo} The donation amount in cents, the charity name, a
 *   `hasDonation` flag that is true only when a positive donation amount exists,
 *   and a `donationOptedIn` flag mirroring the metadata opt-in (true on refund
 *   rows even though their amount is zero).
 */
export const getDonationInfo = (source: DonationInfoSource): DonationInfo => {
	const metadata = source?.metadata
	const fromColumn =
		source != null && typeof source.donationAmount === 'number' ? source.donationAmount : null
	const fromMetadata = Math.max(0, parseInt(metadata?.donationAmount ?? '0', 10) || 0)
	const donationAmount = Math.max(0, fromColumn ?? fromMetadata)
	return {
		hasDonation: donationAmount > 0,
		donationOptedIn: metadata?.donationOptedIn === 'true',
		donationAmount,
		donationCharityName: metadata?.donationCharityName || undefined
	}
}

// Postgres exposes these as numeric columns, Firestore as metadata strings.
type AmountCents = number | string | null | undefined

const resolveCents = (value: AmountCents): number | null =>
	typeof value === 'number' ? toFiniteCents(value) : parseFiniteCents(value ?? undefined)

export type PaymentPrincipalSource = {
	amount?: AmountCents
	amountWithoutSurcharge?: AmountCents
	baseAmount?: AmountCents
	metadata?: { baseAmount?: string } | null
	tipAmount?: number
	donationAmount?: number
	isRefund?: boolean
}

/**
 * Returns what the practice was actually paid in cents, with surcharge, donation
 * and tip stripped out so each gets its own receipt line. Returns `null` when
 * there is no usable amount, so callers pick their own empty state.
 *
 * `baseAmount` is already net of both; on the fallback path the caller has to pass
 * `tipAmount`/`donationAmount` for them to come off.
 *
 * The refundable counterpart is `getRefundablePrincipalAmount` below.
 */
export const getPaymentPrincipalAmount = (source: PaymentPrincipalSource): number | null => {
	const amount = resolveCents(source.amount)
	if (!amount) return null

	// baseAmount already excludes donation and tip. Not on refunds: they inherit the payment's
	// metadata, so it would show the full original amount instead of the refunded one (DASH-9064).
	const resolvedBase = resolveCents(source.baseAmount) ?? resolveCents(source.metadata?.baseAmount)
	if (!source.isRefund && resolvedBase != null && resolvedBase > 0) {
		return resolvedBase
	}

	// Refund rows are negative, and a refund's 0 is a real $0 service portion (tip and surcharge
	// refunds both write it), so only an absent value falls back. On a payment 0 means unset.
	const withoutSurcharge = resolveCents(source.amountWithoutSurcharge)
	const principal =
		withoutSurcharge != null && (source.isRefund || withoutSurcharge > 0)
			? withoutSurcharge
			: amount

	// Refunds skip the subtraction — a tip refund is its own row, so this is already tip-free.
	return Math.max(
		0,
		Math.abs(principal) -
			(source.isRefund ? 0 : (source.tipAmount ?? 0) + (source.donationAmount ?? 0))
	)
}

type RefundablePrincipalSource = {
	isSurchargeEnabled: boolean
	amount: number
	amountWithoutSurcharge?: number | null
	baseAmount?: number | null
	tipAmount?: number | null
	metadata?: Record<string, string> | null
}

/**
 * Returns the refundable service principal in cents, excluding non-refundable
 * surcharge (via amountWithoutSurcharge), donation and tip portions. When a donation
 * was included (`donationOptedIn` or a positive donation amount), prefers
 * `baseAmount` from the PaymentHistory view or PI metadata (DASH-8214/DASH-8246).
 */
export const getRefundablePrincipalAmount = (
	source: RefundablePrincipalSource,
	donationInfo: DonationInfo
): number => {
	const { isSurchargeEnabled, amount, amountWithoutSurcharge, baseAmount, tipAmount, metadata } =
		source
	const donationOptedIn = metadata?.donationOptedIn === 'true'
	const normalizedAmount = toFiniteCents(amount)
	const tipCents = toFiniteCents(tipAmount) ?? parseFiniteCents(metadata?.tipAmount) ?? 0

	if (donationOptedIn || donationInfo.hasDonation) {
		const resolvedBase = toFiniteCents(baseAmount) ?? parseFiniteCents(metadata?.baseAmount)

		if (resolvedBase != null) {
			return Math.max(0, resolvedBase)
		}

		const principalWithoutSurcharge =
			isSurchargeEnabled && amountWithoutSurcharge != null
				? toFiniteCents(amountWithoutSurcharge)
				: normalizedAmount

		if (principalWithoutSurcharge == null) {
			return 0
		}

		const donationCents = toFiniteCents(donationInfo.donationAmount) ?? 0
		return Math.max(0, principalWithoutSurcharge - donationCents - tipCents)
	}

	if (isSurchargeEnabled && amountWithoutSurcharge != null) {
		const withoutSurcharge = toFiniteCents(amountWithoutSurcharge)
		if (withoutSurcharge != null) {
			return Math.max(0, withoutSurcharge - tipCents)
		}
	}

	return Math.max(0, (normalizedAmount ?? 0) - tipCents)
}

type SurchargeProrationBaseSource = {
	amountWithoutSurcharge?: number | null
	baseAmount?: number | null
	metadata?: Record<string, string> | null
}

/**
 * Returns the denominator to prorate a refund's surcharge portion against, in cents.
 * The surcharge is collected on the donation-EXCLUSIVE base, but `amountWithoutSurcharge`
 * is donation-INCLUSIVE by design (`base + donation`), so using it under-refunds the
 * surcharge. Mirrors the backend's `surchargeProrationBase`.
 */
export const getSurchargeProrationBase = (
	source: SurchargeProrationBaseSource,
	donationInfo: DonationInfo
): number => {
	const resolvedBase =
		toFiniteCents(source.baseAmount) ?? parseFiniteCents(source.metadata?.baseAmount)
	// Falsy, not null, check — so 0 falls through, mirroring the backend's `||`.
	if (resolvedBase) return resolvedBase

	const billAmountWithoutSurcharge =
		toFiniteCents(source.amountWithoutSurcharge) ??
		parseFiniteCents(source.metadata?.amountWithoutSurcharge) ??
		0
	// Not clamped at 0: a negative makes prorateSurchargeRefund return 0, as the backend does.
	return billAmountWithoutSurcharge - (toFiniteCents(donationInfo.donationAmount) ?? 0)
}

export type RemainingRefundablePayment = DonationInfoSource & {
	amount?: number | null
	amount_captured?: number | null
	amountCaptured?: number | null
	amount_refunded?: number | null
	amountRefunded?: number | null
	amountRefundedWithoutSurcharge?: number | null
	amountWithoutSurcharge?: number | null
	baseAmount?: number | null
	tipAmount?: number | null
	refundedTipAmount?: number | null
	surcharge?: boolean | null
	metadata?: Record<string, string> | null
	refunds?: { data?: Array<{ metadata?: Record<string, string> }> } | null
}

export const getAmountPreviouslyRefundedInCents = (
	source: RemainingRefundablePayment,
	isSurchargeEnabled: boolean
): number => {
	if (isSurchargeEnabled) {
		if (source.refunds?.data?.length) {
			return source.refunds.data.reduce((total, refund) => {
				return total + (parseFiniteCents(refund.metadata?.refundAmountWithoutSurcharge) ?? 0)
			}, 0)
		}
		return toFiniteCents(source.amountRefundedWithoutSurcharge) ?? 0
	}

	// amountRefunded is Stripe's total across every refund on the charge, so it also counts tip
	// refunds. The surcharge branch above is already tip-free (a tip refund writes
	// refundAmountWithoutSurcharge: 0), but this one has to net them out, or refunding the tip
	// would eat into what is still refundable on the payment itself.
	const totalRefunded =
		toFiniteCents(source.amountRefunded) ?? toFiniteCents(source.amount_refunded) ?? 0
	return Math.max(0, totalRefunded - (toFiniteCents(source.refundedTipAmount) ?? 0))
}

const getReleasedAmountInCents = (
	amount: number | null | undefined,
	amountCaptured: number | null | undefined
): number => {
	const hold = toFiniteCents(amount)
	const captured = toFiniteCents(amountCaptured)
	if (hold == null || captured == null) return 0
	return Math.max(0, hold - captured)
}

/**
 * Remaining refundable principal in cents. Takes off prior refunds or the part of the hold that
 * was never captured, whichever is larger: when Stripe records a released remainder as a refund it
 * is already inside the refunded total, so subtracting both would count it twice.
 * @param refundablePrincipal The hold's refundable principal, without surcharge, tip or donation.
 * @param amountPreviouslyRefunded Prior refunds in the same principal terms.
 * @param amount The authorized amount of the hold.
 * @param amountCaptured What was actually captured.
 * @returns The remaining refundable principal, never below zero.
 */
export const getRemainingPrincipalInCents = (
	refundablePrincipal: number,
	amountPreviouslyRefunded: number,
	amount: number | null | undefined,
	amountCaptured: number | null | undefined
): number => {
	const released = getReleasedAmountInCents(amount, amountCaptured)
	return Math.max(0, refundablePrincipal - Math.max(amountPreviouslyRefunded, released))
}

/**
 * Returns remaining refundable service principal in cents after prior refunds.
 * Matches the max-refund math used by RefundModal / RefundRequestModal.
 */
export const getRemainingRefundableAmountInCents = (
	payment: RemainingRefundablePayment
): number => {
	const donationInfo = getDonationInfo(payment)
	const metadata = payment.metadata ?? undefined
	const metadataSurcharge = metadata?.surcharge === 'true'
	const isSurchargeEnabled =
		isSurchargePayment(payment as PaymentIntent | RefundCharge | PaymentHistory | Stripe.Charge) ||
		metadataSurcharge

	const refundablePrincipal = getRefundablePrincipalAmount(
		{
			isSurchargeEnabled,
			amount: toFiniteCents(payment.amount) ?? 0,
			amountWithoutSurcharge:
				toFiniteCents(payment.amountWithoutSurcharge) ??
				parseFiniteCents(metadata?.amountWithoutSurcharge),
			baseAmount: payment.baseAmount,
			tipAmount: payment.tipAmount,
			metadata
		},
		donationInfo
	)

	return getRemainingPrincipalInCents(
		refundablePrincipal,
		getAmountPreviouslyRefundedInCents(payment, isSurchargeEnabled),
		payment.amount,
		payment.amountCaptured ?? payment.amount_captured
	)
}

export const hasRemainingRefundableAmount = (payment: RemainingRefundablePayment): boolean =>
	getRemainingRefundableAmountInCents(payment) > 0

export const isRefundPayment = (paymentIntent: PaymentIntent | RefundCharge | PaymentHistory) =>
	paymentIntent.object === 'refund'

export const isRevertedPayment = (paymentIntent: PaymentHistory) =>
	isRefundPayment(paymentIntent) && paymentIntent.refundType === 'reversal'

export const isSavedPaymentMethodAdded = (
	paymentIntent:
		| PaymentIntent
		| RefundCharge
		| PaymentHistory
		| PaymentIntentDto
		| { metadata?: Record<string, string> }
) => paymentIntent?.metadata?.savedPaymentMethodAdded === 'true'
